// SCR-STAFF-102 — فیلدهای سفارشی مراجعه‌کننده (AP-PAT-02، US-PAT-006): تعریف از CFG (`ICustomFieldQuery` — AP-PLT-05) و مقدار در
// PAT؛ اعتبارسنجی نوع، گزینه و الزامی بودن سمت سرور (خطای فیلد `customFields.{key}`).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap, type CustomFieldValueView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { formatJalaliInput, parseJalaliInput, patKeys } from './common';

type Value = string | number | boolean | string[] | null;

function toInput(item: CustomFieldValueView): Value {
  const raw = item.value as unknown;
  switch (item.definition.type) {
    case 'Boolean':
      return raw === true;
    case 'MultiSelect':
      return Array.isArray(raw) ? (raw as string[]) : [];
    case 'Date':
      return typeof raw === 'string' ? formatJalaliInput(raw) : '';
    case 'Number':
      return typeof raw === 'number' ? String(raw) : '';
    default:
      return typeof raw === 'string' ? raw : '';
  }
}

function toPayload(item: CustomFieldValueView, value: Value): unknown {
  switch (item.definition.type) {
    case 'Boolean':
      return value === true;
    case 'MultiSelect':
      return Array.isArray(value) && value.length > 0 ? value : null;
    case 'Date':
      return typeof value === 'string' && value.trim() ? (parseJalaliInput(value) ?? value) : null;
    case 'Number':
      return typeof value === 'string' && value.trim() ? Number(value) : null;
    default:
      return typeof value === 'string' && value.trim() ? value.trim() : null;
  }
}

export function PatientCustomFieldsPanel({ patientId }: { patientId: string }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('pat.patient.update');
  const fields = useQuery({
    queryKey: patKeys.customFields(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/patients/patients/{id}/custom-fields', { params: { path: { id: patientId } } })),
  });
  const [values, setValues] = useState<Record<string, Value>>({});
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (fields.data) {
      setValues(Object.fromEntries(fields.data.items.map((item) => [item.definition.key, toInput(item)])));
    }
  }, [fields.data]);
  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      unwrap(
        tenantApi.PUT('/api/v1/patients/patients/{id}/custom-fields', {
          params: { path: { id: patientId } },
          body: { values: payload as Record<string, never> },
        }),
      ),
    onMutate: () => setSaved(false),
    onSuccess: (data) => {
      queryClient.setQueryData(patKeys.customFields(session?.tenantId, patientId), data);
      setSaved(true);
    },
  });
  const items = (fields.data?.items ?? []).filter((item) => item.definition.isActive || item.value !== null);
  if (fields.isSuccess && items.length === 0) {
    return null;
  }
  // خطای سرور: PHX-GEN-0001 با field = customFields.{key} (CustomFieldValidation).
  const errorField = isApiError(save.error) && typeof save.error.extensions['field'] === 'string' ? save.error.extensions['field'] : null;
  const fieldError = (key: string): string | undefined => (errorField === `customFields.${key}` ? t('pat:custom_fields.invalid') : undefined);
  const set = (key: string, value: Value) => setValues((current) => ({ ...current, [key]: value }));
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="custom-fields">
      <Typography variant="h2" gutterBottom>
        {t('pat:custom_fields.title')}
      </Typography>
      {fields.error ? <ErrorState error={fields.error} compact onRetry={() => void fields.refetch()} /> : null}
      <Stack spacing={2}>
        {saved && <Alert severity="success">{t('pat:custom_fields.saved')}</Alert>}
        {items.map((item) => {
          const { definition } = item;
          const value = values[definition.key] ?? null;
          const error = fieldError(definition.key);
          const common = {
            label: definition.label,
            required: definition.isRequired,
            disabled: !write.canWrite,
            error: Boolean(error),
            helperText: error,
            fullWidth: true,
          };
          switch (definition.type) {
            case 'Boolean':
              return (
                <FormControlLabel
                  key={definition.key}
                  control={<Checkbox checked={value === true} disabled={!write.canWrite} onChange={(event) => set(definition.key, event.target.checked)} />}
                  label={definition.label}
                />
              );
            case 'SingleSelect':
              return (
                <TextField key={definition.key} select {...common} value={value ?? ''} onChange={(event) => set(definition.key, event.target.value)}>
                  <MenuItem value="">{t('pat:fields.none')}</MenuItem>
                  {definition.options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              );
            case 'MultiSelect':
              return (
                <TextField
                  key={definition.key}
                  select
                  {...common}
                  value={Array.isArray(value) ? value : []}
                  onChange={(event) => set(definition.key, event.target.value as unknown as string[])}
                  slotProps={{ select: { multiple: true } }}
                >
                  {definition.options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              );
            default:
              return (
                <TextField
                  key={definition.key}
                  {...common}
                  value={typeof value === 'string' ? value : ''}
                  onChange={(event) => set(definition.key, event.target.value)}
                  helperText={error ?? (definition.type === 'Date' ? t('pat:fields.birth_date_help') : undefined)}
                  slotProps={{
                    htmlInput: definition.type === 'Text' ? {} : { dir: 'ltr', inputMode: 'numeric' },
                  }}
                />
              );
          }
        })}
        {save.error && !errorField ? <ErrorState error={save.error} compact /> : null}
        {write.canWrite && items.length > 0 && (
          <div>
            <Button
              variant="contained"
              disabled={save.isPending}
              onClick={() =>
                save.mutate(Object.fromEntries(items.map((item) => [item.definition.key, toPayload(item, values[item.definition.key] ?? null)])))
              }
              data-testid="custom-fields-save"
            >
              {t('common:actions.save')}
            </Button>
          </div>
        )}
      </Stack>
    </Paper>
  );
}
